import moment from 'moment';
import XLSX from 'xlsx';
import type { Types as CoreTypes } from '../../../core/types';
import type { BaseOptions } from '../../../common/types';
import { getVertexId } from '../../../core/util';

type Schedule = {
  division:  CoreTypes.Deep.Division;
  settings:  CoreTypes.Deep.Settings;
  groups:    CoreTypes.Deep.Group[];
  teachers:  CoreTypes.Deep.Teacher[];
  locations: CoreTypes.Deep.Location[];
  courses:   CoreTypes.Deep.Course[];
  events:    CoreTypes.Deep.Event[];
  periods:   CoreTypes.Deep.Period[];
  persons:   CoreTypes.Deep.Person[];
};



export default (
  schedule: Schedule,
): XLSX.WorkBook => {
  const options: BaseOptions = { idKey: 'id' };
  // must have a division id
  const did = getVertexId(schedule.division, options);
  if (!did) throw new Error('division id is undefined');

  // a map of period id -> period
  const periodsMap = new Map(schedule.periods?.map((p) => [getVertexId(p, options) as string | undefined, p as Pick<CoreTypes.Deep.Period, 'ranges'>]));
  const defaultPeriod: Pick<CoreTypes.Deep.Period, 'ranges'> = {
    ranges: [{ start: schedule.division.start, end: schedule.division.end }]
  };
  periodsMap.set(undefined, defaultPeriod);

  // a map of course id -> course
  const coursesMap = new Map(schedule.courses?.map((c) => [getVertexId(c, options), c]));

  // a map of location id -> location
  const locationsMap = new Map(schedule.locations?.map((l) => [getVertexId(l, options), l]));

  // get start and end dates from division in YYYY-MM-DD format
  const startDate = moment.utc(schedule.division.start).format('YYYY-MM-DD');
  const endDate = moment.utc(schedule.division.end).format('YYYY-MM-DD');
  // get schoolyear from division start date in format YY/YY (only considers the start year, for example 2023-01-01 becomes 23/24)
  const schoolYear = moment.utc(schedule.division.start).format('YY') + '/' +
    moment.utc(schedule.division.start).add(1, 'year').format('YY');

  const holidays = schedule.settings?.calendarExceptions
    ?.filter((x) => x.start && x.end);


  const holidaysInDays: moment.Moment[] = [];

  if (holidays) {
    for (const holiday of holidays) {
      const start = moment.utc(holiday.start);
      const end = moment.utc(holiday.end);
      while (start.isBefore(end)) {
        // Only push weekdays (Monday to Friday)
        if (start.isoWeekday() < 6) {
          holidaysInDays.push(start.clone());
        }
        start.add(1, 'day');
      }
    }
  }

  ////
  //// Activities (events)
  ////
  const activities = schedule.events
    ?.map((e) => {
      // must have start and end
      if (!e.start || !e.end) return null;
      const start = moment.utc(e.start);
      const end = moment.utc(e.end);

      // must have course
      const course = e.course ? coursesMap.get(getVertexId(e.course, options)) : undefined;
      if (!course) return null;
      const courseName = course.displayName ?? '';

      // Location
      const inLocation = e.inLocations?.at(0);
      const location = inLocation ? locationsMap.get(getVertexId(inLocation, options)) : undefined;
      const locationName = location?.displayName;

      const _period = e.period ?? course.period ?? schedule.settings.period;
      const period = periodsMap.get(_period ? getVertexId(_period, options) : undefined);

      const weeksInPeriod = period?.ranges
        .map((x) => {
          // fetch all weeks between start and end
          const start = moment.utc(x.start);
          const end = moment.utc(x.end);
          const weeks = new Array<number>();
          while (start.isBefore(end)) {
            weeks.push(start.week());
            start.add(1, 'week');
          }
          return weeks;
        })
        .flat();

      const allWeeks = defaultPeriod?.ranges
        .map((x) => {
          // fetch all weeks between start and end
          const start = moment.utc(x.start);
          const end = moment.utc(x.end);
          const weeks = new Array<number>();
          while (start.isBefore(end)) {
            weeks.push(start.week());
            start.add(1, 'week');
          }
          return weeks;
        })
        .flat();

      const excludedWeeks = allWeeks
        .filter((x) => !weeksInPeriod?.includes(x))
        .join(', ');

      // Get the holidays in the period (don't include holidays that are not in the period)
      const holidaysInPeriod = holidaysInDays
        .filter((x) => {
          return weeksInPeriod?.includes(x.week());
        })
        .map((x) => x.format('YYYY-MM-DD'))
        .join(', ');

      return {
        DayOfWeek:               (start.day() + 6) % 7,
        Duration:                end.diff(start, 'minutes'),
        EndDate:                 endDate,
        Name:                    courseName,
        StartDate:               startDate,
        StartTime:               start.format('HH:mm'),
        ExcludeDates:            holidaysInPeriod,
        ExcludeWeeks:            excludedWeeks,
        ExternalActivityID:      e.ids,
        ExternalLocationID:      '',
        ExternalTeachingGroupID: '',
        Location:                locationName,
        SchoolYear:              schoolYear,
        Teachers:                ''
      };
    })
    .filter((x): x is NonNullable<typeof x> => !!x);

  ////
  //// store in workbook
  ////
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(activities), 'Activities');

  return wb;
};
